import React from 'react';
import { TaskAttemptReviewStatus } from '@scale/shared/defaults';
import { Icon, Typography } from '@scale/scaleui';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { SpeedAuditLookupResponse } from '@scale/shared/types/corp/attemptAudit';

interface ChatBulkAuditAttemptItemProps {
  isAuditing: boolean;
  isLastAttempt: boolean;
  attemptNumber: number;
  attemptName: string;
  isSelected: boolean;
  reviewStatus?: TaskAttemptReviewStatus;
  onClick?: () => void;
  setSelectedAttempt: () => void;
}

interface ChatBulkAuditAttemptListProps {
  attempts?: SpeedAuditLookupResponse[];
  selectedAttempt: number;
  setSelectedAttempt: (index: number) => void;

  setOpen: (value: boolean) => void;
}

export const reviewStatusIcon: Record<string, IconProp> = {
  [TaskAttemptReviewStatus.Accepted]: ['far', 'check-circle'],
  [TaskAttemptReviewStatus.Rejected]: ['far', 'times-circle'],
  [TaskAttemptReviewStatus.Fixed]: ['far', 'pen-circle'],
};

export const reviewStatusText: Record<string, string> = {
  [TaskAttemptReviewStatus.Accepted]: 'Accepted',
  [TaskAttemptReviewStatus.Rejected]: 'Rejected',
  [TaskAttemptReviewStatus.Fixed]: 'Accepted with edits',
};

export function isSuccessStatus(reviewStatus: TaskAttemptReviewStatus | undefined): boolean {
  return (
    reviewStatus === TaskAttemptReviewStatus.Accepted ||
    reviewStatus === TaskAttemptReviewStatus.Fixed
  );
}

export function getAttemptString(attemptNumber: number): string {
  let attemptString = '';
  switch (attemptNumber) {
    case 1:
      attemptString = '1st attempt';
      break;
    case 2:
      attemptString = '2nd attempt';
      break;
    case 3:
      attemptString = '3rd attempt';
      break;
    default:
      attemptString = `${attemptNumber}th attempt`;
      break;
  }
  return attemptString;
}

const ChatBulkAuditAttemptItem = React.memo(function ChatBulkAuditAttemptItem(
  props: ChatBulkAuditAttemptItemProps,
) {
  const { isAuditing, isLastAttempt, attemptNumber, attemptName, reviewStatus, isSelected } = props;

  return (
    <div className="flex flex-row items-start gap-2">
      <div className="flex-column items-center justify-center bg-neutral-200:hover">
        <Icon className="m-0 p-0 w-2 h-2" variant="neutral" icon={['far', 'circle']} />
        {!isLastAttempt && (
          <div className="m-auto border-solid border-l border-neutral-200 flex-1 h-12 w-0"></div>
        )}
      </div>
      <div
        className="flex-column w-full items-start -mt-1 cursor-pointer"
        onClick={() => {
          props.setSelectedAttempt();
        }}
      >
        <Typography.Paragraph3 className="text-neutral-400">
          {isAuditing ? 'Auditing (latest)' : getAttemptString(attemptNumber)}
        </Typography.Paragraph3>
        <div className="flex flex-row items-center gap-2">
          <Typography.Paragraph2>{attemptName}</Typography.Paragraph2>
          <div className="bg-neutral-100 flex flex-row gap-0 items-center justify-center pr-2 rounded-md">
            <Icon
              icon={reviewStatusIcon[reviewStatus ?? ''] ?? ['far', 'pen-circle']}
              size="sm"
              variant={isSuccessStatus(reviewStatus) ? 'success' : 'neutral'}
              rounded={true}
            />
            <Typography.Paragraph3
              className={
                'font-semibold ' + (isSuccessStatus(reviewStatus) ? 'text-success-800' : '')
              }
            >
              {reviewStatusText[reviewStatus ?? ''] ?? 'Pending'}
            </Typography.Paragraph3>
          </div>
          {isSelected && (
            <div className="flex-grow my-auto ml-auto">
              <Icon icon={['far', 'check']} variant="primary" size="sm" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export const ChatBulkAuditAttemptList = React.memo(function ChatBulkAuditAttemptList(
  props: ChatBulkAuditAttemptListProps,
) {
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  return (
    <div
      className="scaleui shadow-lg flex flex-col p-4 fixed rounded-lg"
      style={{ zIndex: 999, backgroundColor: '#fff' }}
      ref={wrapperRef}
    >
      {props.attempts &&
        props.attempts.map((attempt, index) => (
          <ChatBulkAuditAttemptItem
            key={attempt.name + index}
            isAuditing={index === 0}
            isLastAttempt={index === props.attempts!.length - 1}
            attemptNumber={props.attempts!.length - index}
            attemptName={attempt.name}
            reviewStatus={attempt.reviewStatus}
            isSelected={index === props.selectedAttempt}
            setSelectedAttempt={() => props.setSelectedAttempt(index)}
          />
        ))}
    </div>
  );
});
